import WaitlistCountry from './WaitlistCountry';
import WaitlistSize from './WaitlistSize';
import WaitlistTerms from './WaitlistTerms';

class WaitListContent {
  constructor(product, color, disclaimer) {
    this.product = product;
    this.productColor = color;
    this.disclaimer = disclaimer;
    this.node = null;
    this.#init();
  }

  /**
   * Intializes the waitlist modal content
   */
  #init() {
    this.node = document.createElement('div');
    this.node.classList.add('WaitlistModal__outer');

    const podZero = document.createElement('div');
    podZero.appendChild(this.#buildForm());

    this.node.appendChild(podZero);
  }

  /**
   * Builds the close icon element
   * @returns {HTMLElement} - The close icon element
   */
  #buildCloseIcon() {
    const closeIcon = document.createElement('div');
    closeIcon.classList.add('WaitlistModal__close');
    closeIcon.innerHTML = `
      <span class="WaitlistModal__close-icon"></span>
      <span class="WaitlistModal__close-icon"></span>
    `;
    closeIcon.addEventListener('click', () => {
      const waitlistWrapper = document.querySelector('.WaitlistModal__wrapper');
      if (waitlistWrapper) {
        waitlistWrapper.innerHTML = '';
      }
    });

    return closeIcon;
  }

  /**
   * Builds the heading element
   * @param {String} text - The text for the heading
   * @returns {HTMLElement} - The heading element
   */
  #buildHeading(text) {
    const heading = document.createElement('h1');
    heading.classList.add('WaitlistModal__title');
    heading.textContent = text;

    return heading;
  }

  /**
   * Builds the subheading element
   * @param {String} text - The text for the subheading
   * @returns {HTMLElement} - The subheading element
   */
  #buildSubheading(text) {
    const subheading = document.createElement('p');
    subheading.classList.add('WaitlistModal__description');
    subheading.textContent = text;

    return subheading;
  }

  /**
   * Builds the submit button element
   * @param {String} text - The text for the submit button
   * @param {Boolean} isConfirmation - Helps us to determine if the modal is in confirmation state
   * @returns {HTMLElement} - The submit button element
   */
  #buildSubmitButton(text, isConfirmation = false) {
    const submitButton = document.createElement('button');
    submitButton.classList.add('WaitlistModal__button-submit', 'Button', 'Button--primary');
    submitButton.innerText = text;
    if (!isConfirmation) {
      submitButton.id = 'WaitlistEmailSubmit';
      submitButton.type = 'submit';
      submitButton.setAttribute('data-product-name', this.product.title);
      submitButton.setAttribute('data-product-id', this.product.id);
    } else {
      submitButton.addEventListener('click', () => {
        const waitlistWrapper = document.querySelector('.WaitlistModal__wrapper');
        if (waitlistWrapper) {
          waitlistWrapper.innerHTML = '';
        }
      });
    }

    return submitButton;
  }

  /**
   * Builds the waitlist form element
   * @returns {HTMLElement} - The waitlist form element
   */
  #buildForm() {
    const waitlistForm = document.createElement('form');
    waitlistForm.classList.add('WaitlistModal__form');
    waitlistForm.id = 'WaitlistForm';
    waitlistForm.addEventListener('submit', this.#submitForm.bind(this));

    waitlistForm.appendChild(this.#buildCloseIcon());
    waitlistForm.appendChild(this.#buildHeading('Join the Waitlist'));
    waitlistForm.appendChild(
      this.#buildSubheading(`Select your size below and we'll email you when it becomes available!`),
    );
    waitlistForm.appendChild(this.#buildDetails());

    // Error message
    const errorMessage = document.createElement('p');
    errorMessage.classList.add('WaitlistModal__error-message');
    waitlistForm.appendChild(errorMessage);

    // Submit button
    waitlistForm.appendChild(this.#buildSubmitButton('Join the Waitlist'));

    return waitlistForm;
  }

  /**
   * Build an optimized featured image for the selected variant
   * @param {Obj} selectedVariant - Variant object
   * @returns {String} - The optimized featured image URL
   */
  #getFeaturedImage(selectedVariant) {
    const featuredImage = selectedVariant.featured_image
      ? selectedVariant.featured_image.src
      : this.product.featured_image;
    let ext = featuredImage.includes('.webp') ? '.webp' : '.jpg';
    const featuredImageSplit = featuredImage.split(ext);
    featuredImageSplit[0] += '_500x750_crop_center';
    const productImage = featuredImageSplit.join(ext);

    return productImage;
  }

  /**
   * Builds the swatch image URL for the selected variant
   * @param {Obj} selectedVariant - Variant object
   * @returns {String} - The swatch image URL
   */
  #buildSwatchUrl(selectedVariant) {
    return `https://cdn.shopify.com/s/files/1/0070/8853/7651/t/8/assets/${selectedVariant.barcode}_SWT_48x48_crop_center.jpg?v=${window.theme.swatches_updated_at}`;
  }

  /**
   * Builds the image element for the selected variant
   * @param {Obj} selectedVariant - Variant object
   * @returns {HTMLElement} - The image element
   */
  #buildImage(selectedVariant) {
    const imagePod = document.createElement('div');
    imagePod.classList.add('WaitlistModal__image-container');
    const imageItem = document.createElement('img');
    imageItem.classList.add('WaitlistModal__variant-image');
    imageItem.src = this.#getFeaturedImage(selectedVariant);
    imagePod.appendChild(imageItem);

    return imagePod;
  }

  /**
   * Builds the variant title element
   * @param {String} productTitle - The title of the product
   * @returns {HTMLElement} - The variant title element
   */
  #buildVariantTitle(productTitle) {
    const heading = document.createElement('h2');
    heading.classList.add('WaitlistModal__variant-title');
    heading.innerText = productTitle;

    return heading;
  }

  /**
   * Builds the details pod containing product information
   * @returns {HTMLElement} - The details pod containing product information
   */
  #buildDetails() {
    const detailsPod = document.createElement('div');
    detailsPod.classList.add('WaitlistModal__details');

    // Building details for left side
    const detailsLeftSide = document.createElement('div');
    detailsLeftSide.classList.add('WaitlistModal__details-half');

    const selectedVariant = this.product.variants.find((variant) => variant.option1 === this.productColor);
    if (!selectedVariant) {
      console.error('There is no selected variant');
      return;
    }

    const variantInfo = document.createElement('div');
    variantInfo.classList.add('WaitlistModal__variant-info');

    const label = document.createElement('span');
    label.classList.add('WaitlistModal__variant-color--label');
    label.innerText = 'Color';

    const value = document.createElement('span');
    value.classList.add('WaitlistModal__variant-color');
    value.innerText = this.productColor;

    const colorSwatch = document.createElement('span');
    colorSwatch.classList.add('WaitlistModal__variant-swatch');
    colorSwatch.style.backgroundImage = `url(${this.#buildSwatchUrl(selectedVariant)})`;

    variantInfo.appendChild(this.#buildVariantTitle(this.product.title));
    variantInfo.appendChild(label);
    variantInfo.appendChild(value);
    variantInfo.appendChild(colorSwatch);

    // Building details for right side
    const detailsRightSide = document.createElement('div');
    detailsRightSide.classList.add('WaitlistModal__details-half');

    // Heading
    const headingTwo = document.createElement('div');
    headingTwo.classList.add('WaitlistModal__text--secondary');
    headingTwo.innerText = 'Select Your Size';

    // Sizes
    const sizesPod = document.createElement('div');
    sizesPod.classList.add('WaitlistModal__size-container');

    this.product.variants.forEach((variant) => {
      if (window.inventory980) {
        if (
          variant.option1 === this.productColor &&
          !window.inventory980.color[variant.id] &&
          variant.option2 !== 'NS'
        ) {
          const sizeInstance = new WaitlistSize(variant.option2, variant.sku);
          sizesPod.appendChild(sizeInstance.node);
        }
      } else if (variant.option1 === this.productColor && !variant.available && variant.option2 !== 'NS') {
        const sizeInstance = new WaitlistSize(variant.option2, variant.sku);
        sizesPod.appendChild(sizeInstance.node);
      }
    });

    // Email input field
    const emailPod = document.createElement('div');
    emailPod.classList.add('WaitlistModal__email');

    const inputEmailField = document.createElement('input');
    inputEmailField.id = 'WaitlistEmail';
    inputEmailField.classList.add('WaitlistModal__email-input');
    inputEmailField.type = 'email';
    inputEmailField.name = 'email';
    inputEmailField.placeholder = 'name@company.com';
    inputEmailField.addEventListener('change', () => {
      if (inputEmailField.classList.contains('WaitlistModal__email-error')) {
        inputEmailField.classList.remove('WaitlistModal__email-error');
      }
    });

    emailPod.appendChild(inputEmailField);

    // Country selector and terms conditions
    const countrySelector = new WaitlistCountry();
    const termsInstance = new WaitlistTerms(this.disclaimer);

    // Hidden input field element
    const inputHiddenField = document.createElement('input');
    inputHiddenField.type = 'hidden';
    inputHiddenField.id = 'WaitlistSource';
    inputHiddenField.value = 'waitlist';

    // Appending nodes on details left side
    detailsLeftSide.appendChild(this.#buildImage(selectedVariant));
    detailsLeftSide.appendChild(variantInfo);

    // Appending nodes on details right side
    detailsRightSide.appendChild(headingTwo);
    detailsRightSide.appendChild(sizesPod);
    detailsRightSide.appendChild(emailPod);
    detailsRightSide.appendChild(inputHiddenField);
    detailsRightSide.appendChild(countrySelector.node);
    detailsRightSide.appendChild(termsInstance.node);

    // Overall details
    detailsPod.appendChild(detailsLeftSide);
    detailsPod.appendChild(detailsRightSide);

    return detailsPod;
  }

  /**
   * Builds a waitlist confirmation content
   * @param {String} email - The email address
   * @param {Array} sizes - The selected sizes
   */
  #buildConfirmation(email, sizes) {
    const podZero = document.createElement('div');
    podZero.appendChild(this.#buildCloseIcon());
    podZero.appendChild(this.#buildHeading(`You're On the List!`));
    podZero.appendChild(
      this.#buildSubheading(`We'll notify you at ${email} when this product becomes available again.`),
    );
    podZero.appendChild(this.#buildConfirmationDetails(sizes));
    podZero.appendChild(this.#buildSubmitButton('Continue Shopping', true));

    this.node.appendChild(podZero);
    this.node.classList.add('WaitlistModal__confirmation');
  }

  /**
   * Builds the confirmation details section
   * @param {Array} sizes - The selected sizes
   * @returns {HTMLElement} - The confirmation details element
   */
  #buildConfirmationDetails(sizes) {
    const detailsPod = document.createElement('div');
    detailsPod.classList.add('WaitlistModal__details');

    // Left side of details
    const detailsLeftSide = document.createElement('div');
    detailsLeftSide.classList.add('WaitlistModal__details-half');

    const selectedVariant = this.product.variants.find((variant) => variant.option1 === this.productColor);
    if (!selectedVariant) {
      console.error('There is no selected variant');
      return;
    }

    // Right side of details
    const detailsRightSide = document.createElement('div');
    detailsRightSide.classList.add('WaitlistModal__details-half');

    const variantRowColor = document.createElement('div');
    variantRowColor.classList.add('WaitlistModal__variant-row');
    variantRowColor.innerHTML = `
      <span class="WaitlistModal__variant-color--label">Color </span>
      <span class="WaitlistModal__variant-color">${this.productColor}</span>
    `;

    const variantRowSize = document.createElement('div');
    variantRowSize.classList.add('WaitlistModal__variant-row');
    variantRowSize.innerHTML = `
      <span class="WaitlistModal__variant-color--label">Size </span>
      <span class="WaitlistModal__variant-color">${sizes.join(', ')}</span>
    `;

    // Appending nodes on details left side
    detailsLeftSide.appendChild(this.#buildImage(selectedVariant));

    // Appending nodes on details right side
    detailsRightSide.appendChild(this.#buildVariantTitle(this.product.title));
    detailsRightSide.appendChild(variantRowColor);
    detailsRightSide.appendChild(variantRowSize);

    // Overall details
    detailsPod.appendChild(detailsLeftSide);
    detailsPod.appendChild(detailsRightSide);

    return detailsPod;
  }

  getNode() {
    return this.node;
  }

  /**
   * validates the waitlist form
   * @param {*} form - The waitlist form element
   * @returns {Obj} - An object containing validation errors
   */
  #validateForm(form) {
    let errorMessages = [];
    const errors = {
      size: false,
      country: false,
      email: false,
      checkbox: false,
      messages: '',
    };
    const expression =
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const selectedSizes = form.querySelectorAll('.WaitlistModal__size--active');
    if (!selectedSizes.length) {
      errorMessages.push('select a size');
      errors.size = true;
    }

    const email = form.querySelector('#WaitlistEmail');
    if (!email.value) {
      errorMessages.push('enter your email address');
      errors.email = true;
    } else if (!expression.test(String(email.value).toLowerCase())) {
      errorMessages.push('enter a valid email address');
      errors.email = true;
    }

    if (window.theme.country_iso !== 'US') {
      const countrySelector = document.querySelector('#WaitlistCountry').value;
      if (!countrySelector) {
        errorMessages.push('select a country');
        errors.country = true;
      }
    }

    const marketingCheckbox = document.querySelector('input[name="waitlist_marketing"]');
    if (!marketingCheckbox.checked) {
      errorMessages.push('select checkbox to agree to waitlist');
      errors.checkbox = true;
    }

    if (errorMessages.length > 0) {
      errorMessages = errorMessages.join(', ');
      errors.messages = `Oops! Please fix the following errors: ${errorMessages}`;
    }

    return errors;
  }

  /**
   * Sends the waitlist form data to the server
   * @param {HTMLFormElement} form - The waitlist form element
   */
  #sendForm(form) {
    const email = form.querySelector('#WaitlistEmail').value;
    const sizes = form.querySelectorAll('.WaitlistModal__size--active');
    const skus = [];
    const selectedSizes = [];
    sizes.forEach((size) => {
      skus.push(size.dataset.sku);
      selectedSizes.push(size.innerText);
    });

    // const marketingCheckbox = document.querySelector('input[name="waitlist_marketing"]');
    const domain = window.location.host;
    const url =
      domain === 'www.windsorstore.com'
        ? 'https://shopify-inventory-service.wndsr.io/waitlist'
        : 'https://shopify-inventory-service.wndsr.dev/waitlist';

    const data = {
      contact: email,
      contact_type: 'email',
      skus: skus,
    };

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.text())
      .then((data) => {
        this.showConfirmation = true;
        window.dataLayer.push({
          event: 'add_to_waitlist_submit',
          product_name: this.product.title,
          product_id: this.product.id,
        });
        const waitlistOuter = document.querySelector('.WaitlistModal__outer');
        waitlistOuter.innerHTML = '';
        this.#buildConfirmation(email, selectedSizes);
        return JSON.stringify(data);
      })
      .catch(() => {
        console.error('Error in sending request over to waitlist.');
      });
  }

  /**
   * Evalutes and submits the waitlist form
   * @param {Event} e - The submit event
   */
  #submitForm(e) {
    e.preventDefault();
    const form = e.target;
    const errors = this.#validateForm(form);

    if (errors.size) {
      const availableSizes = form.querySelectorAll('.WaitlistModal__size');
      availableSizes.forEach((size) => {
        size.classList.add('WaitlistModal__size-error');
      });
    }

    if (errors.email) {
      const emailInput = form.querySelector('#WaitlistEmail');
      emailInput.classList.add('WaitlistModal__email-error');
    }

    if (errors.country) {
      const countrySelector = form.querySelector('#WaitlistCountry');
      countrySelector.classList.add('WaitlistModal__country-error');
    }

    if (errors.checkbox) {
      const termsCheckbox = form.querySelector('input[name="waitlist_marketing"]');
      const termsText = form.querySelector('.WaitlistModal__terms-wrapper');
      termsCheckbox.classList.add('WaitlistModal__checkbox-error');
      termsText.classList.add('WaitlistModal__terms-wrapper-error');
    }

    const errorMessage = form.querySelector('.WaitlistModal__error-message');
    if (errors.messages) {
      errorMessage.textContent = errors.messages;
      return;
    } else {
      errorMessage.textContent = '';
    }

    this.#sendForm(form);
  }
}

export default WaitListContent;
